
import {
  defineComponent, ref, computed, watch, onMounted, PropType,
} from 'vue';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import { useI18n } from 'vue-i18n';
import type {
  ImportAdapter, ImportContext, FieldDefinition, ImportFieldMapping,
} from '@/composables/import/types';
import { showToastError } from '@/helpers';
import { nestCall, NestApiType } from '@/composables/nestApi';
import SpmMessage from '@/components/SpmUI/SpmMessage.vue';

interface TargetFieldOption {
  label: string;
  value: string;
  required: boolean;
  isIdentifier: boolean;
  group?: string;
}

interface VapidKeyOption {
  label: string;
  value: string;
  isDefault: boolean;
}

export default defineComponent({
  name: 'ColumnMappingStep',

  components: {
    Dropdown,
    Checkbox,
    SpmMessage,
  },

  props: {
    /** Source column headers from the file */
    sourceHeaders: {
      type: Array as PropType<string[]>,
      required: true,
    },

    /** First row of data for preview */
    firstDataRow: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },

    /** Import adapter */
    adapter: {
      type: Object as PropType<ImportAdapter>,
      required: true,
    },

    /** Import type (contact, custom_data, etc.) */
    importType: {
      type: String,
      required: true,
    },

    /** Additional params for the adapter */
    params: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },

    /** Shop ID for fetching VAPID keys */
    shopId: {
      type: [Number, String] as PropType<number | string>,
      default: null,
    },

    /** Current mapping (v-model) */
    modelValue: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
  },

  emits: ['update:modelValue', 'mapping-change', 'valid-change', 'vapid-key-change'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const loading = ref(true);
    const targetFields = ref<FieldDefinition[]>([]);

    // Local mapping: sourceColumn -> targetField
    const localMapping = ref<Record<string, string>>({});

    // VAPID key state
    const selectedVapidKeyId = ref<string | null>(null);
    const vapidKeyOptions = ref<VapidKeyOption[]>([]);
    const loadingVapidKeys = ref(false);

    // Selected columns for import (checkbox state)
    const selectedColumns = ref<Record<string, boolean>>({});

    // ==========================================================================
    // COMPUTED PROPERTIES
    // ==========================================================================

    /**
     * Count of selected columns
     */
    const selectedColumnsCount = computed(() => Object.values(selectedColumns.value).filter(Boolean).length);

    /**
     * Convert target fields to dropdown options
     * For contacts: translate labels via i18n
     * For custom_data: use labels directly from schema
     */
    const targetFieldOptions = computed((): TargetFieldOption[] => targetFields.value.map((field) => {
      let label = field.name;
      if (field.label) {
        // For contacts, label is a translation key
        // For custom_data, label is the actual label from schema
        label = props.importType === 'contact' ? t(field.label) : field.label;
      }
      return {
        label,
        value: field.name,
        required: field.required,
        isIdentifier: field.isIdentifier || false,
        group: field.group,
      };
    }));

    /**
     * Interface for grouped dropdown options
     */
    interface GroupedOptions {
      label: string;
      items: TargetFieldOption[];
    }

    /**
     * Get already mapped target fields (to exclude from other dropdowns)
     */
    const mappedTargetFields = computed(() => {
      const mapped = new Set<string>();
      Object.values(localMapping.value).forEach((target) => {
        if (target) mapped.add(target);
      });
      return mapped;
    });

    /**
     * Get identifier fields
     */
    const identifierFields = computed(() => targetFields.value.filter((f) => f.isIdentifier));

    /**
     * Get required fields (non-identifier)
     */
    const requiredFields = computed(() => targetFields.value.filter((f) => f.required && !f.isIdentifier));

    /**
     * Push notification fields
     */
    const PUSH_FIELDS = ['push_endpoint', 'push_key', 'push_token'];

    /**
     * Check push notification mapping status
     */
    const pushMappingStatus = computed(() => {
      const mappedTargets = new Set(Object.values(localMapping.value).filter(Boolean));
      const mappedPushFields = PUSH_FIELDS.filter((f) => mappedTargets.has(f));

      return {
        count: mappedPushFields.length,
        isComplete: mappedPushFields.length === 3,
        isPartial: mappedPushFields.length > 0 && mappedPushFields.length < 3,
        isEmpty: mappedPushFields.length === 0,
        mappedFields: mappedPushFields,
        missingFields: PUSH_FIELDS.filter((f) => !mappedTargets.has(f)),
      };
    });

    /**
     * Check if a field is a push notification field
     */
    const isPushField = (fieldName: string): boolean => PUSH_FIELDS.includes(fieldName);

    /**
     * Check if at least one identifier is mapped
     */
    const hasIdentifierMapped = computed(() => {
      const mappedTargets = new Set(Object.values(localMapping.value).filter(Boolean));

      // For contacts: email OR phone_number OR (push_endpoint AND push_key AND push_token)
      if (props.importType === 'contact') {
        if (mappedTargets.has('email') || mappedTargets.has('phone_number')) {
          return true;
        }
        // Check push notification (all 3 required)
        if (pushMappingStatus.value.isComplete) {
          return true;
        }
        return false;
      }

      // For other types: any identifier is enough
      return identifierFields.value.some((f) => mappedTargets.has(f.name));
    });

    /**
     * Check if all required fields are mapped
     */
    const allRequiredFieldsMapped = computed(() => requiredFields.value.every((field) => {
      const mappedTargets = new Set(Object.values(localMapping.value).filter(Boolean));
      return mappedTargets.has(field.name);
    }));

    /**
     * Check if all selected columns have a mapping
     */
    const allSelectedColumnsHaveMapping = computed(() => props.sourceHeaders.every((header) => {
      // If column is not selected, it doesn't need a mapping
      if (!selectedColumns.value[header]) return true;
      // If column is selected, it must have a mapping
      return !!localMapping.value[header];
    }));

    /**
     * Get selected columns without mapping (for warning)
     */
    const selectedColumnsWithoutMapping = computed(() => props.sourceHeaders.filter(
      (header) => selectedColumns.value[header] && !localMapping.value[header],
    ));

    /**
     * Check if mapping is valid
     */
    const isValid = computed(() => {
      // Must have at least one mapping
      const hasMappings = Object.values(localMapping.value).some(Boolean);
      if (!hasMappings) return false;

      // All selected columns must have a mapping
      if (!allSelectedColumnsHaveMapping.value) return false;

      // Push notification must be complete (all 3) or empty (none)
      if (pushMappingStatus.value.isPartial) return false;

      // If push fields are complete, a VAPID key must be selected
      if (pushMappingStatus.value.isComplete && !selectedVapidKeyId.value) return false;

      // For contacts: need identifier
      if (props.importType === 'contact') {
        return hasIdentifierMapped.value;
      }

      // For custom data: need all required fields
      return allRequiredFieldsMapped.value;
    });

    /**
     * Validation message to display
     */
    const validationMessage = computed(() => {
      if (isValid.value) {
        return t('import.mapping.validMapping');
      }

      // Check if selected columns are missing mapping
      if (!allSelectedColumnsHaveMapping.value) {
        const count = selectedColumnsWithoutMapping.value.length;
        return t('import.mapping.selectedColumnsNeedMapping', { count });
      }

      // Check for partial push notification mapping
      if (pushMappingStatus.value.isPartial) {
        return t('import.mapping.pushIncomplete');
      }

      if (props.importType === 'contact' && !hasIdentifierMapped.value) {
        return t('import.mapping.needIdentifier');
      }

      if (!allRequiredFieldsMapped.value) {
        return t('import.mapping.needRequiredFields');
      }

      return t('import.mapping.needMapping');
    });

    // ==========================================================================
    // METHODS
    // ==========================================================================

    /**
     * Get available target fields for a source column (grouped)
     * Excludes already mapped fields (except the current one)
     */
    const getAvailableTargetFields = (sourceColumn: string): GroupedOptions[] => {
      const currentTarget = localMapping.value[sourceColumn];

      // Filter available options
      const availableOptions = targetFieldOptions.value.filter(
        (option) => !mappedTargetFields.value.has(option.value) || option.value === currentTarget,
      );

      // Group them
      const groups: Record<string, TargetFieldOption[]> = {};
      availableOptions.forEach((option) => {
        const groupKey = option.group || 'import.contact.groups.additional';
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(option);
      });

      // Define group order
      const groupOrder = [
        'import.contact.groups.identification',
        'import.contact.groups.consent',
        'import.contact.groups.additional',
      ];

      return groupOrder
        .filter((key) => groups[key]?.length > 0)
        .map((key) => ({
          label: t(key),
          items: groups[key],
        }));
    };

    /**
     * Get available target fields as flat list (for custom_data - no groups)
     * Excludes already mapped fields (except the current one)
     */
    const getAvailableFlatTargetFields = (sourceColumn: string): TargetFieldOption[] => {
      const currentTarget = localMapping.value[sourceColumn];

      return targetFieldOptions.value.filter(
        (option) => !mappedTargetFields.value.has(option.value) || option.value === currentTarget,
      );
    };

    /**
     * Get field label by name
     * For contacts: uses i18n translation
     * For custom_data: uses the label directly from schema
     */
    const getFieldLabel = (fieldName: string): string => {
      const field = targetFields.value.find((f) => f.name === fieldName);
      if (field?.label) {
        // For contacts, label is a translation key
        // For custom_data, label is the actual label from schema
        if (props.importType === 'contact') {
          return t(field.label);
        }
        return field.label;
      }
      return fieldName;
    };

    /**
     * Check if a field is required
     */
    const isFieldRequired = (fieldName: string): boolean => {
      const field = targetFields.value.find((f) => f.name === fieldName);
      return field?.required || false;
    };

    /**
     * Get format hint for a field (if any)
     */
    const getFieldFormatHint = (fieldName: string): string | null => {
      const field = targetFields.value.find((f) => f.name === fieldName);
      if (field?.formatHint) {
        return t(field.formatHint);
      }
      return null;
    };

    /**
     * Truncate preview value
     */
    const truncateValue = (value: unknown): string => {
      const str = String(value);
      return str.length > 30 ? `${str.substring(0, 30)}...` : str;
    };

    /**
     * Auto-map columns based on strict name matching (only high confidence matches)
     * Only runs for contact import type
     */
    const performInitialAutoMapping = () => {
      if (props.importType !== 'contact') return;

      const newMapping: Record<string, string> = {};

      // Define strict mappings: column name variations -> property name
      const strictMappings: Record<string, string> = {
        // Email
        email: 'email',
        mail: 'email',
        emailaddress: 'email',
        adresseemail: 'email',
        adressemail: 'email',
        courriel: 'email',
        // Phone
        phone: 'phone_number',
        phonenumber: 'phone_number',
        telephone: 'phone_number',
        tel: 'phone_number',
        mobile: 'phone_number',
        numerotelephone: 'phone_number',
        numerotel: 'phone_number',
        // First name
        firstname: 'first_name',
        prenom: 'first_name',
        givenname: 'first_name',
        // Last name
        lastname: 'last_name',
        nom: 'last_name',
        familyname: 'last_name',
        surname: 'last_name',
        // Birth date
        birthdate: 'birth_date',
        datedenaissance: 'birth_date',
        datenaissance: 'birth_date',
        birthday: 'birth_date',
        // Language
        language: 'language',
        langue: 'language',
        lang: 'language',
        // City
        city: 'city',
        ville: 'city',
        // Country
        country: 'country',
        pays: 'country',
        // Postal code
        postalcode: 'postal_code',
        zipcode: 'postal_code',
        codepostal: 'postal_code',
        zip: 'postal_code',
        // Company
        company: 'company',
        entreprise: 'company',
        societe: 'company',
        // Address
        address: 'address_line1',
        adresse: 'address_line1',
        addressline1: 'address_line1',
        adresseligne1: 'address_line1',
        address1: 'address_line1',
        adresse1: 'address_line1',
        addressline2: 'address_line2',
        adresseligne2: 'address_line2',
        address2: 'address_line2',
        adresse2: 'address_line2',
        // Region
        region: 'region',
        state: 'region',
        departement: 'region',
        province: 'region',
        // Consents
        emailconsent: 'email_consent',
        consentementemail: 'email_consent',
        smsconsent: 'sms_consent',
        conssentementsms: 'sms_consent',
        whatsappconsent: 'whatsapp_consent',
        consentementwhatsapp: 'whatsapp_consent',
        // Push
        pushendpoint: 'push_endpoint',
        pushkey: 'push_key',
        pushtoken: 'push_token',
      };

      props.sourceHeaders.forEach((header) => {
        // Normalize: lowercase, remove spaces, underscores, dashes
        const headerNormalized = header.toLowerCase().replace(/[_\s-]/g, '');

        // Check for strict match
        const targetProperty = strictMappings[headerNormalized];

        if (targetProperty && !Object.values(newMapping).includes(targetProperty)) {
          newMapping[header] = targetProperty;
        }
      });

      localMapping.value = newMapping;
    };

    /**
     * Handle column selection change
     */
    const onColumnSelectionChange = (header: string) => {
      // If column is deselected, clear its mapping
      if (!selectedColumns.value[header]) {
        delete localMapping.value[header];
      }
    };

    /**
     * Initialize selected columns (all selected by default)
     */
    const initializeSelectedColumns = () => {
      props.sourceHeaders.forEach((header) => {
        if (selectedColumns.value[header] === undefined) {
          selectedColumns.value[header] = true;
        }
      });
    };

    /**
     * Fetch VAPID keys from the API
     */
    const fetchVapidKeys = async () => {
      if (!props.shopId) return;
      loadingVapidKeys.value = true;
      try {
        const response = await nestCall('get', NestApiType.DEFAULT, `/shop/vapid-keys/${props.shopId}`);
        if (response?.success && Array.isArray(response.data)) {
          vapidKeyOptions.value = response.data.map((k: any) => ({
            label: k.label || t('import.mapping.vapidKey.unnamed'),
            value: k.id,
            isDefault: k.is_default,
          }));
          // Auto-select the default key
          const defaultKey = vapidKeyOptions.value.find((k) => k.isDefault);
          if (defaultKey && !selectedVapidKeyId.value) {
            selectedVapidKeyId.value = defaultKey.value;
          }
        }
      } catch (err) {
        console.error('Error fetching VAPID keys:', err);
      } finally {
        loadingVapidKeys.value = false;
      }
    };

    /**
     * Fetch target fields from adapter
     */
    const fetchTargetFields = async () => {
      loading.value = true;

      try {
        const ctx: ImportContext = {
          importType: props.importType,
          params: props.params,
        };

        if (props.adapter.getFields) {
          targetFields.value = await Promise.resolve(props.adapter.getFields(ctx));
        }
      } catch (err) {
        console.error('Error fetching target fields:', err);
        await showToastError(t('import.mapping.fetchError'));
      } finally {
        loading.value = false;
      }
    };

    /**
     * Build mapping DTO for parent
     */
    const buildMappingDto = (): ImportFieldMapping[] => {
      const mappingDto: ImportFieldMapping[] = [];

      Object.entries(localMapping.value).forEach(([source, target]) => {
        // Only include selected columns
        if (target && selectedColumns.value[source]) {
          const field = targetFields.value.find((f) => f.name === target);
          // Get the display label for the target field
          const targetLabel = getFieldLabel(target);
          mappingDto.push({
            source,
            target,
            required: field?.required || false,
            targetLabel,
          });
        }
      });

      return mappingDto;
    };

    // ==========================================================================
    // WATCHERS
    // ==========================================================================

    // Sync with parent modelValue
    watch(() => props.modelValue, (newVal) => {
      if (JSON.stringify(newVal) !== JSON.stringify(localMapping.value)) {
        localMapping.value = { ...newVal };
      }
    }, { deep: true });

    // Emit changes to parent when mapping changes
    watch(localMapping, (newVal) => {
      emit('update:modelValue', { ...newVal });
      emit('mapping-change', buildMappingDto());
      emit('valid-change', isValid.value);
    }, { deep: true });

    // Emit changes when selected columns change
    watch(selectedColumns, () => {
      emit('mapping-change', buildMappingDto());
      emit('valid-change', isValid.value);
    }, { deep: true });

    // Emit validity changes
    watch(isValid, (newVal) => {
      emit('valid-change', newVal);
    });

    // Watch push mapping status to fetch VAPID keys when all 3 push fields are mapped
    watch(() => pushMappingStatus.value.isComplete, (isComplete) => {
      if (isComplete && vapidKeyOptions.value.length === 0) {
        fetchVapidKeys();
      }
      if (!isComplete) {
        selectedVapidKeyId.value = null;
      }
    });

    // Emit VAPID key change
    watch(selectedVapidKeyId, (newVal) => {
      const selectedOption = vapidKeyOptions.value.find((k) => k.value === newVal);
      emit('vapid-key-change', {
        id: newVal,
        label: selectedOption?.label || null,
      });
      // Re-emit validity when VAPID key changes
      emit('valid-change', isValid.value);
    });

    // ==========================================================================
    // LIFECYCLE
    // ==========================================================================

    onMounted(async () => {
      await fetchTargetFields();

      // Initialize selected columns (all selected by default)
      initializeSelectedColumns();

      // Initialize from modelValue if provided, otherwise perform auto-mapping
      if (Object.keys(props.modelValue).length > 0) {
        localMapping.value = { ...props.modelValue };
      } else {
        // Perform initial auto-mapping for contact import
        performInitialAutoMapping();
      }
    });

    return {
      t,
      loading,
      localMapping,
      selectedColumns,
      selectedColumnsCount,
      targetFields,
      isValid,
      validationMessage,
      isPushField,
      pushMappingStatus,
      selectedVapidKeyId,
      vapidKeyOptions,
      loadingVapidKeys,
      getAvailableTargetFields,
      getAvailableFlatTargetFields,
      getFieldLabel,
      isFieldRequired,
      getFieldFormatHint,
      truncateValue,
      onColumnSelectionChange,
    };
  },
});
